import React from "react";
import { Box, Stack, Typography, IconButton, useMediaQuery } from "@mui/material";
import {
  FolderOpen,
  Plus,
  X,
  MoreVertical,
  Edit,
  Trash2,
  CalendarDays,
  CheckCircle2,
  Clock,
  BarChart3,
  User,
  AlertTriangle,
  Target,
  Megaphone,
  Globe,
  Share2,
  PenTool,
  Zap,
  Rocket,
  Settings,
  AlertCircle,
  ChevronDown,
} from "lucide-react";
import { useTheme } from "@mui/material/styles";
import { renderToStaticMarkup } from "react-dom/server";

import { kanbanColors } from "../../../theme/theme";
import { Badge } from "../../../components/Badge";
import { ReadOnlyBadge } from "../../../components/ReadOnlyBadge";
import { Button } from "../../../components/Button";
import { Progress } from "../../../components/Progress";
import { ReadOnlyCardTooltip } from "../../../components/ReadOnlyCardTooltip";
import { readOnlyCardSx } from "../../../utils/clientAccess";
import {
  DropdownMenuContent,
  DropdownMenuItem,
} from "../../../components/DropdownMenu";
import { DocumentationLinkList } from "../../../components/DocumentationLinkList";
import {
  Collapsible,
  CollapsibleTrigger,
  CollapsibleContent,
} from "../../../components/Collapsible";
import { statusLabels } from "../../../components/data/mockData";


// Mapeo de estados de proyectos a colores del Badge
const projectStatusColorMap = {
  planning: "yellow",
  "in-progress": "blue",
  completed: "green",
};

// Fondo común de las columnas y color de acento por estado
const KANBAN_COLUMN_BG = kanbanColors.columnSurface;
const KANBAN_ACCENTS = {
  not_started: "#9CA3AF",
  completed: "#0F9D58",
  no_necesario: "#EF4444",
};

// Cabecera de columna: puntito de color + título + contador
const KanbanColumnHeader = ({ column, count, sx }) => (
  <Stack direction="row" alignItems="center" spacing={1} sx={sx}>
    <Box
      sx={{
        width: 8,
        height: 8,
        borderRadius: "50%",
        backgroundColor: column.accent,
        flexShrink: 0,
      }}
    />
    <Typography sx={{ fontSize: "0.95rem", fontWeight: 700, color: "text.primary" }}>
      {column.title}
    </Typography>
    <Typography sx={{ fontSize: "0.875rem", color: "text.secondary" }}>
      {count}
    </Typography>
  </Stack>
);


export const KanbanView = ({
  project,
  tasks,
  currentUser,
  onClose,
  onNewTask,
  onEditTask,
  onDeleteTask,
  onDragStart,
  onDragOver,
  onDrop,
  isPhaseView = false, // Nueva prop para indicar si son fases o tareas
  isPhaseTaskView = false, // Nueva prop para indicar si son tareas de una fase específica
  onTaskStatusUpdate, // Función para actualizar tareas localmente
  readOnly = false,
  phaseName, // Nombre de la fase si es vista de tareas de fase
  phaseNumber, // Número de la fase si es vista de tareas de fase
  columnScroll = null, // { maxHeight: '80vh' } — aplica scroll vertical por columna
  isOnlyTasksMode = false, // Nueva prop para indicar modo solo tareas
}) => {
  const theme = useTheme();
  const canEdit = currentUser?.role !== "client" && !readOnly;

  // Detección de móvil para vista de cliente
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isClientMobile = currentUser?.role === 'client' && isMobile;

  // Estado para columnas expandidas en vista móvil
  const [expandedColumns, setExpandedColumns] = React.useState({
    not_started: false,
    in_progress: false,
    completed: false,
    no_necesario: false,
  });

  const [menuAnchorEl, setMenuAnchorEl] = React.useState(null);
  const [selectedTaskForMenu, setSelectedTaskForMenu] = React.useState(null);
  const menuOpen = Boolean(menuAnchorEl);

  const handleMenuOpen = (event, task) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setSelectedTaskForMenu(task);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedTaskForMenu(null);
  };

  // Columnas para fases y tareas (TechnicalSetup). Todas comparten el fondo gris/lila;
  // el color de cada estado vive en el puntito de la cabecera y en el lateral de las tarjetas.
  const phaseColumns = [
    { id: "not_started", title: "Pendiente", accent: KANBAN_ACCENTS.not_started },
    { id: "in_progress", title: "En Progreso", accent: theme.palette.primary.main },
    { id: "completed", title: "Completado", accent: KANBAN_ACCENTS.completed },
    { id: "no_necesario", title: "No Necesario", accent: KANBAN_ACCENTS.no_necesario },
  ];
  const TaskphaseColumns = phaseColumns;

  // Usar columnas y tareas según el tipo de vista
  const currentColumns = isPhaseTaskView
    ? currentUser?.role === "client"
      ? TaskphaseColumns.filter((col) => col.id !== "no_necesario")
      : TaskphaseColumns
    : isPhaseView
      ? currentUser?.role === "client"
        ? phaseColumns.filter((col) => col.id !== "no_necesario")
        : phaseColumns
      : phaseColumns;
  const currentTasks = tasks;

  const formatDate = (dateString) => {
    if (!dateString) return "Sin fecha";
    return new Date(dateString).toLocaleDateString("es-ES", {
      day: "numeric",
      month: "short",
    });
  };

  if (!project) return null;

  // Vista móvil para cliente - Columnas como secciones colapsables
  if (isClientMobile) {
    return (
      <Box
        sx={{
          position: "fixed",
          inset: 0,
          backgroundColor: theme.palette.background.default,
          zIndex: 10000,
          overflow: "auto",
        }}
      >
        <Stack sx={{ minHeight: "100vh" }}>
          {/* Header del Kanban - Compacto para móvil */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "16px",
              borderBottom: `1px solid ${theme.palette.divider}`,
              backgroundColor: theme.palette.background.paper,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  backgroundColor: `${theme.palette.primary.main}1a`,
                  borderRadius: "8px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <FolderOpen size={20} color={theme.palette.primary.main} />
              </Box>
              <Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontSize: "1rem",
                    fontWeight: 500,
                    lineHeight: 1.2,
                  }}
                >
                  {isPhaseTaskView && !isOnlyTasksMode
                    ? `Fase ${phaseNumber} - ${phaseName}`
                    : project.name}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.text.secondary,
                    fontSize: "0.75rem",
                  }}
                >
                  {isPhaseTaskView && !isOnlyTasksMode
                    ? `Tareas de la fase`
                    : project.clientName}
                </Typography>
              </Box>
            </Stack>

            <Button variant="outline" onClick={onClose} size="sm">
              <X size={16} />
            </Button>
          </Box>

          {/* Columnas como secciones colapsables */}
          <Box sx={{ padding: 2, paddingBottom: "80px", display: "flex", flexDirection: "column", gap: 2 }}>
            {currentColumns.map((column) => {
              const columnTasks = currentTasks.filter((t) => t.status === column.id);
              const isExpanded = expandedColumns[column.id] || false;

              return (
                <Collapsible key={column.id}>
                  <Box
                    sx={{
                      backgroundColor: KANBAN_COLUMN_BG,
                      borderRadius: "14px",
                      overflow: "hidden",
                    }}
                  >
                    <CollapsibleTrigger
                      onClick={() =>
                        setExpandedColumns((prev) => ({
                          ...prev,
                          [column.id]: !prev[column.id],
                        }))
                      }
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          padding: 2,
                          cursor: "pointer",
                        }}
                      >
                        <KanbanColumnHeader column={column} count={columnTasks.length} />
                        <ChevronDown
                          size={20}
                          style={{
                            transform: isExpanded
                              ? "rotate(180deg)"
                              : "rotate(0deg)",
                            transition: "transform 0.2s ease",
                            color: theme.palette.text.secondary,
                          }}
                        />
                      </Box>
                    </CollapsibleTrigger>

                    <CollapsibleContent open={isExpanded}>
                      <Box sx={{ padding: 2, paddingTop: 0 }}>
                        {columnTasks.length === 0 ? (
                          <Typography
                            variant="body2"
                            sx={{
                              color: theme.palette.text.secondary,
                              textAlign: "center",
                              py: 2,
                            }}
                          >
                            Sin tareas
                          </Typography>
                        ) : (
                          <Stack spacing={1.5}>
                            {columnTasks.map((task) => (
                              <Box
                                key={task.id}
                                sx={{
                                  backgroundColor: theme.palette.background.paper,
                                  padding: "14px 16px",
                                  borderRadius: "10px",
                                  borderLeft: `3px solid ${column.accent}`,
                                  boxShadow: "0 1px 2px 0 rgba(17, 12, 34, 0.06)",
                                }}
                              >
                                {/* Título de la tarea */}
                                <Stack
                                  direction="row"
                                  alignItems="center"
                                  spacing={1}
                                  sx={{ mb: isPhaseView ? 1 : 0.5 }}
                                >
                                  {isPhaseView && task.icon && (
                                    <task.icon
                                      size={18}
                                      color={theme.palette.primary.main}
                                    />
                                  )}
                                  <Typography
                                    variant="body1"
                                    sx={{
                                      fontSize: isPhaseView ? "0.875rem" : "0.8rem",
                                      fontWeight: 500,
                                      flex: 1,
                                    }}
                                  >
                                    {task.title}
                                  </Typography>
                                </Stack>

                                {/* Contenido según tipo de vista */}
                                {isPhaseView && task.tasksCount !== undefined ? (
                                  // Vista de fases con progreso
                                  <Box>
                                    <Stack
                                      direction="row"
                                      justifyContent="space-between"
                                      alignItems="center"
                                      sx={{ mb: 0.5 }}
                                    >
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          fontSize: "0.7rem",
                                          color: theme.palette.text.secondary,
                                        }}
                                      >
                                        {task.completedTasksCount}/{task.tasksCount} tareas
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          fontSize: "0.7rem",
                                          fontWeight: 500,
                                          color: theme.palette.primary.main,
                                        }}
                                      >
                                        {task.progress}%
                                      </Typography>
                                    </Stack>
                                    <Progress value={task.progress} sx={{ height: "4px" }} />
                                  </Box>
                                ) : isPhaseTaskView ? (
                                  // Vista de tareas de fase
                                  <Stack spacing={0.25}>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontSize: "0.7rem",
                                        color: theme.palette.text.secondary,
                                      }}
                                    >
                                      Asignado a:{" "}
                                      <Box
                                        component="span"
                                        sx={{
                                          fontWeight: 600,
                                          color: theme.palette.text.primary,
                                        }}
                                      >
                                        {task.assignee}
                                      </Box>
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontSize: "0.7rem",
                                        color: theme.palette.text.secondary,
                                      }}
                                    >
                                      Entrega:{" "}
                                      <Box
                                        component="span"
                                        sx={{
                                          fontWeight: 500,
                                          color: theme.palette.error.main,
                                        }}
                                      >
                                        {formatDate(task.dueDate)}
                                      </Box>
                                    </Typography>
                                  </Stack>
                                ) : (
                                  // Vista de tareas de proyecto
                                  <Stack spacing={0.25}>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontSize: "0.7rem",
                                        color: theme.palette.text.secondary,
                                      }}
                                    >
                                      {task.assignee}
                                    </Typography>
                                    <Stack
                                      direction="row"
                                      alignItems="center"
                                      spacing={0.5}
                                    >
                                      <CalendarDays
                                        size={10}
                                        color={theme.palette.text.secondary}
                                      />
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          fontSize: "0.7rem",
                                          fontWeight: 500,
                                          color: theme.palette.error.main,
                                        }}
                                      >
                                        {formatDate(task.dueDate)}
                                      </Typography>
                                    </Stack>
                                  </Stack>
                                )}
                              </Box>
                            ))}
                          </Stack>
                        )}
                      </Box>
                    </CollapsibleContent>
                  </Box>
                </Collapsible>
              );
            })}
          </Box>
        </Stack>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        position: "fixed",
        inset: 0,
        backgroundColor: theme.palette.background.default,
        zIndex: 10000,
        overflow: "auto",
      }}
    >
      <Stack sx={{ height: "100%", minHeight: "100vh" }}>
        {/* Header del Kanban */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "32px",
            borderBottom: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.background.paper,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={2}>
            <Box
              sx={{
                width: 56,
                height: 56,
                backgroundColor: `${theme.palette.primary.main}1a`,
                borderRadius: "12px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <FolderOpen size={28} color={theme.palette.primary.main} />
            </Box>
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontSize: "1.5rem",
                  fontWeight: 500,
                }}
              >
                {isPhaseTaskView && !isOnlyTasksMode
                  ? `Fase ${phaseNumber} - ${phaseName}`
                  : project.name}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: theme.palette.text.secondary,
                  fontSize: "1.125rem",
                }}
              >
                {isPhaseTaskView && !isOnlyTasksMode
                  ? `Tareas específicas de la fase seleccionada`
                  : project.clientName}
              </Typography>
            </Box>
            <Badge
              color={projectStatusColorMap[project.status]}
              sx={{
                fontWeight: 500,
              }}
            >
              {statusLabels[project.status]}
            </Badge>
            {readOnly && <ReadOnlyBadge />}
          </Stack>

          <Stack direction="row" alignItems="center" spacing={2}>
            <Button variant="outline" onClick={onClose} size="md">
              <X size={16} />
            </Button>
          </Stack>
        </Box>

        {/* Columnas del Kanban */}
        <Box sx={{ flex: "1 0 auto", padding: "32px", minHeight: 0 }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: `repeat(${currentColumns.length}, 1fr)`,
              gap: "16px",
              alignItems: "stretch",
              height: "100%",
            }}
          >
            {currentColumns.map((column) => (
              <Box
                key={column.id}
                sx={{
                  backgroundColor: KANBAN_COLUMN_BG,
                  borderRadius: "16px",
                  padding: "16px",
                  minHeight: "200px",
                  ...(columnScroll ? { maxHeight: columnScroll.maxHeight || '80vh' } : {}),
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                }}
                onDragOver={canEdit ? onDragOver : undefined}
                onDrop={
                  canEdit
                    ? (e) => {
                      if (isPhaseTaskView && onTaskStatusUpdate) {
                        // Para tareas de fase, actualizar localmente primero
                        try {
                          const draggedTask = JSON.parse(
                            e.dataTransfer.getData("text/plain") || "{}"
                          );
                          if (
                            draggedTask &&
                            draggedTask.id &&
                            draggedTask.status !== column.id
                          ) {
                            onTaskStatusUpdate(draggedTask.id, column.id);
                          }
                        } catch (error) {
                          console.error(
                            "Error parsing dragged task data:",
                            error
                          );
                        }
                      }
                      // Llamar a la función externa
                      onDrop && onDrop(e, column.id);
                    }
                    : undefined
                }
              >
                <KanbanColumnHeader
                  column={column}
                  count={currentTasks.filter((t) => t.status === column.id).length}
                  sx={{ mb: 2, px: 0.5 }}
                />

                <Stack spacing={1.5} sx={{ flex: 1, ...(columnScroll ? { overflowY: 'auto', minHeight: 0 } : {}) }}>
                  {currentTasks
                    .filter((task) => task.status === column.id)
                    .map((task) => (
                      <ReadOnlyCardTooltip key={task.id} active={readOnly}>
                        <Box
                          sx={{
                            backgroundColor: theme.palette.background.paper,
                            padding: "16px",
                            borderRadius: "12px",
                            borderLeft: `3px solid ${column.accent}`,
                            boxShadow: "0 1px 2px 0 rgba(17, 12, 34, 0.06)",
                            cursor: canEdit ? "move" : "default",
                            transition: "box-shadow 0.2s",
                            "&:hover": {
                              boxShadow: "0 6px 16px -6px rgba(17, 12, 34, 0.18)",
                            },
                            ...(readOnly && readOnlyCardSx(theme)),
                          }}
                          draggable={canEdit}
                          onDragStart={
                            canEdit
                              ? (e) => {
                                e.dataTransfer.setData(
                                  "text/plain",
                                  JSON.stringify(task)
                                );

                                // Solo para fases - drag preview idéntico en tamaño y estilo
                                if (
                                  isPhaseView &&
                                  task.tasksCount !== undefined
                                ) {
                                  const dragPreview =
                                    document.createElement("div");
                                  const rect =
                                    e.currentTarget.getBoundingClientRect();

                                  dragPreview.style.cssText = `
                                      position: fixed;
                                      top: -9999px;
                                      left: -9999px;
                                      width: ${rect.width}px;
                                      background: ${theme.palette.background.paper};
                                      padding: 16px;
                                      border-radius: 12px;
                                      border-left: 3px solid ${column.accent};
                                      box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
                                      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
                                      box-sizing: border-box;
                                    `;

                                  // Obtener el SVG del icono (usar los componentes importados directamente)
                                  const getIconSVG = (IconComponent) => {
                                    if (!IconComponent) return "";

                                    try {
                                      const element = React.createElement(
                                        IconComponent,
                                        {
                                          size: 20,
                                          color: theme.palette.primary.main,
                                          strokeWidth: 2,
                                          strokeLinecap: "round",
                                          strokeLinejoin: "round",
                                          style: { flexShrink: 0 },
                                        }
                                      );

                                      return renderToStaticMarkup(element);
                                    } catch (err) {
                                      return "";
                                    }
                                  };

                                  // Crear el contenido igual que la tarjeta original
                                  dragPreview.innerHTML = `
                                      <div style="
                                        display: flex;
                                        align-items: flex-start;
                                        justify-content: space-between;
                                        margin-bottom: 10px;
                                      ">
                                        <div style="
                                          display: flex;
                                          align-items: center;
                                          gap: 8px;
                                          flex: 1;
                                          min-width: 0;
                                        ">
                                          ${task.icon ? getIconSVG(task.icon) : ""
                                    }
                                          <h6 style="
                                            font-size: 0.9rem;
                                            font-weight: 500;
                                            margin: 0;
                                            color: ${theme.palette.text.primary};
                                            line-height: 1.6;
                                            flex: 1;
                                            min-width: 0;
                                          ">
                                            ${task.title}
                                          </h6>
                                        </div>
                                      </div>

                                      <div>
                                        <div style="
                                          display: flex;
                                          justify-content: space-between;
                                          align-items: center;
                                          margin-bottom: 8px;
                                        ">
                                          <span style="
                                            font-size: 0.75rem;
                                            color: ${theme.palette.text.secondary
                                    };
                                            line-height: 1.43;
                                          ">
                                            ${task.completedTasksCount}/${task.tasksCount
                                    } tareas
                                          </span>
                                          <span style="
                                            font-size: 0.75rem;
                                            font-weight: 500;
                                            color: ${theme.palette.primary.main};
                                            line-height: 1.43;
                                          ">
                                            ${task.progress}%
                                          </span>
                                        </div>

                                        <div style="
                                          width: 100%;
                                          height: 8px;
                                          background-color: ${theme.palette.mode === "dark"
                                      ? "#374151"
                                      : "#e5e7eb"
                                    };
                                          border-radius: 9999px;
                                          overflow: hidden;
                                        ">
                                          <div style="
                                            height: 100%;
                                            width: ${task.progress}%;
                                            background-color: ${theme.palette.primary.main
                                    };
                                            border-radius: 9999px;
                                          "></div>
                                        </div>
                                      </div>
                                    `;

                                  document.body.appendChild(dragPreview);

                                  // Calcular offset del cursor
                                  const offsetX = e.clientX - rect.left;
                                  const offsetY = e.clientY - rect.top;

                                  e.dataTransfer.setDragImage(
                                    dragPreview,
                                    offsetX,
                                    offsetY
                                  );

                                  setTimeout(() => {
                                    if (dragPreview.parentNode) {
                                      document.body.removeChild(dragPreview);
                                    }
                                  }, 0);
                                }

                                onDragStart && onDragStart(e, task);
                              }
                              : undefined
                          }
                        >
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="flex-start"
                            sx={{
                              marginBottom:
                                isPhaseView || isPhaseTaskView ? "10px" : "12px",
                            }}
                          >
                            <Stack
                              direction="row"
                              alignItems="center"
                              spacing={1}
                              sx={{ flex: 1, minWidth: 0 }}
                            >
                              {isPhaseView && task.icon && (
                                <task.icon
                                  size={20}
                                  color={theme.palette.primary.main}
                                />
                              )}
                              <Typography
                                variant="h6"
                                sx={{
                                  fontSize:
                                    isPhaseView || isPhaseTaskView
                                      ? "0.9rem"
                                      : "1rem",
                                  fontWeight: 600,
                                  flex: 1,
                                  minWidth: 0,
                                }}
                              >
                                {task.title}
                              </Typography>
                            </Stack>
                            {canEdit &&
                              !isPhaseView &&
                              task.status !== "no_necesario" && (
                                <IconButton
                                  size="small"
                                  onClick={(e) => handleMenuOpen(e, task)}
                                  onMouseDown={(e) => e.stopPropagation()} // Prevenir drag cuando se hace click
                                  sx={{
                                    width: "24px",
                                    height: "24px",
                                    padding: 0,
                                  }}
                                >
                                  <MoreVertical size={14} />
                                </IconButton>
                              )}
                          </Stack>
                          {menuOpen && selectedTaskForMenu?.id === task.id && (
                            <DropdownMenuContent
                              anchorEl={menuAnchorEl}
                              open={menuOpen}
                              onClose={handleMenuClose}
                              align="end"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <DropdownMenuItem
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onEditTask(selectedTaskForMenu);
                                  handleMenuClose();
                                }}
                              >
                                <Edit size={16} style={{ marginRight: "8px" }} />
                                Editar
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          )}

                          {isPhaseView && task.tasksCount !== undefined ? (
                            <Box>
                              <Stack
                                direction="row"
                                justifyContent="space-between"
                                alignItems="center"
                                sx={{ mb: 1 }}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontSize: "0.75rem",
                                    color: theme.palette.text.secondary,
                                  }}
                                >
                                  {task.completedTasksCount}/{task.tasksCount}{" "}
                                  tareas
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontSize: "0.75rem",
                                    fontWeight: 500,
                                    color: theme.palette.primary.main,
                                  }}
                                >
                                  {task.progress}%
                                </Typography>
                              </Stack>
                              <Progress
                                value={task.progress}
                                sx={{ height: "6px" }}
                              />
                            </Box>
                          ) : isPhaseTaskView ? (
                            // Vista compacta para tareas de fase específica
                            <Box>
                              {/* Información de la tarea en formato descriptivo */}
                              <Stack spacing={0.5}>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontSize: "0.75rem",
                                    color: theme.palette.text.secondary,
                                    fontWeight: 500,
                                  }}
                                >
                                  Asignado a:{" "}
                                  <Box
                                    component="span"
                                    sx={{
                                      fontWeight:
                                        task.status === "no_necesario"
                                          ? 700
                                          : 700,
                                      color: theme.palette.text.primary,
                                    }}
                                  >
                                    {task.status === "no_necesario"
                                      ? "N/A"
                                      : task.assignee}
                                  </Box>
                                </Typography>

                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontSize: "0.75rem",
                                    color: theme.palette.text.secondary,
                                  }}
                                >
                                  Fecha de entrega:{" "}
                                  <Box
                                    component="span"
                                    sx={{
                                      fontWeight:
                                        task.status === "no_necesario"
                                          ? 700
                                          : 500,
                                      color:
                                        task.status === "no_necesario"
                                          ? theme.palette.text.primary
                                          : theme.palette.error.main,
                                    }}
                                  >
                                    {task.status === "no_necesario"
                                      ? "N/A"
                                      : formatDate(task.dueDate)}
                                  </Box>
                                </Typography>

                                {/* Enlaces de documentación */}
                                {currentUser?.role !== "client" && (
                                  <DocumentationLinkList
                                    urls={task.documentation_urls}
                                    sx={{ mt: 0.5 }}
                                  />
                                )}
                              </Stack>
                            </Box>
                          ) : (
                            // Vista original para tareas de proyecto
                            <>
                              <Typography
                                variant="body2"
                                sx={{
                                  color: theme.palette.text.secondary,
                                  marginBottom: "8px",
                                  fontSize: "0.8rem",
                                  lineHeight: 1.3,
                                }}
                              >
                                {task.description}
                              </Typography>

                              <Stack spacing={0.5}>
                                <Stack
                                  direction="row"
                                  justifyContent="space-between"
                                  alignItems="center"
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontSize: "0.75rem",
                                      color: theme.palette.text.secondary,
                                    }}
                                  >
                                    {task.assignee}
                                  </Typography>
                                </Stack>

                                <Stack
                                  direction="row"
                                  justifyContent="space-between"
                                  alignItems="center"
                                >
                                  <Stack
                                    direction="row"
                                    alignItems="center"
                                    spacing={0.3}
                                  >
                                    <CalendarDays
                                      size={10}
                                      color={theme.palette.text.secondary}
                                    />
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontSize: "0.75rem",
                                        fontWeight: 500,
                                        color: theme.palette.error.main,
                                      }}
                                    >
                                      {formatDate(task.dueDate)}
                                    </Typography>
                                  </Stack>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontSize: "0.7rem",
                                      color: theme.palette.text.secondary,
                                    }}
                                  >
                                    {formatDate(task.createdDate)}
                                  </Typography>
                                </Stack>
                              </Stack>
                            </>
                          )}
                        </Box>
                      </ReadOnlyCardTooltip>
                    ))}
                </Stack>
              </Box>
            ))}
          </Box>
        </Box>
      </Stack>
    </Box>
  );
};
